import { CapabilitiesCache } from "@/cache/CapabilitiesCache";
import { CustomGeoJSONCache } from "@/cache/CustomGeoJSONCache";
import { GenericCache } from "@/cache/GenericCache";
import { GeoJSONCache } from "@/cache/GeoJSONCache";
import { createWmsTileGetter } from "@/cache/PVSTileService/TileGetters/WMSTileGetter";
import { PlotCache } from "@/cache/PlotCache";
import { PoiCache } from "@/cache/PoiCache";
import { PointJSONCache } from "@/cache/PointJSONCache";
import { PolygonJSONCache } from "@/cache/PolygonJSONCache";
import { TILE_SIZE } from "@/cache/SpatioTemporalTileCache/config";
import { VectorTileCache } from "@/cache/VectorTileCache";
import { KiB, MiB } from "@/utility/bytesize";
import type { CoordinateSystem } from "@mm/api.meteomatics.com";
import { AvailableTimeFrameCache } from "./AvailableTimeFrameCache";
import { ColorMapCache } from "./ColorMapCache";
import { InitDateCache } from "./InitDateCache";
import { MaptilerSpritesCache } from "./MaptilerSpritesCache";
import { createPVSTileService } from "./PVSTileService/PVSTileServiceFactory";
import { createMultiWmsTileGetter } from "./PVSTileService/TileGetters/MultiWMSTileGetter";
import { CACHE_PROFILE_QUERY_PARAM, cacheLimit, getCacheProfile } from "./cacheProfile";
export * from "./CacheDatumState";
export { CACHE_PROFILE_QUERY_PARAM } from "./cacheProfile";

export const CACHE_PROFILE = getCacheProfile();

const GEOJSON_CACHE_MAX_SIZE = MiB(128);
const GEOJSON_CACHE_DELAYED_EVICTION_SIZE = MiB(160);
const CUSTOM_GEOJSON_CACHE_MAX_SIZE = MiB(48);
const CUSTOM_GEOJSON_CACHE_DELAYED_EVICTION_SIZE = MiB(64);
const CUSTOM_GEOJSON_GEOMETRY_CACHE_MAX_SIZE = MiB(64);
const CUSTOM_GEOJSON_GEOMETRY_CACHE_DELAYED_EVICTION_SIZE = MiB(96);
const POI_CACHE_MAX_SIZE = MiB(48);
const POI_CACHE_DELAYED_EVICTION_SIZE = MiB(64);
const POI_DATA_CACHE_MAX_SIZE = MiB(64);
const POI_DATA_CACHE_DELAYED_EVICTION_SIZE = MiB(96);
const VECTOR_TILE_CACHE_MAX_SIZE = MiB(128);
const VECTOR_TILE_CACHE_DELAYED_EVICTION_SIZE = MiB(160);
const VECTOR_TILE_STYLE_CACHE_MAX_SIZE = MiB(8);
const VECTOR_TILE_STYLE_CACHE_DELAYED_EVICTION_SIZE = MiB(12);
const CAPABILITIES_CACHE_MAX_SIZE = MiB(2);
const CAPABILITIES_CACHE_DELAYED_EVICTION_SIZE = MiB(4);
const POINT_JSON_CACHE_MAX_SIZE = MiB(32);
const POINT_JSON_CACHE_DELAYED_EVICTION_SIZE = MiB(48);
const POLYGON_JSON_CACHE_MAX_SIZE = MiB(32);
const POLYGON_JSON_CACHE_DELAYED_EVICTION_SIZE = MiB(48);
const PLOT_CACHE_MAX_SIZE = MiB(32);
const PLOT_CACHE_DELAYED_EVICTION_SIZE = MiB(48);
const INIT_DATE_CACHE_MAX_SIZE = KiB(128);
const INIT_DATE_CACHE_DELAYED_EVICTION_SIZE = KiB(256);
const MAPTILER_SPRITE_CACHE_MAX_SIZE = MiB(64);
const MAPTILER_SPRITE_CACHE_DELAYED_EVICTION_SIZE = MiB(96);

/**
 * Global network caches shared by all scenes and visualization layers.
 *
 * Note: the limits here should be significantly larger than the data that is expected to be 'in use' at once.
 * Otherwise data will be evicted and refetched in each frame. If you want a data structure with tighter memory bounds,
 * that does not evict 'in use' data, check out the `ObjectPool` and `GenerationalObjectPool` classes.
 */
export const networkCaches = {
  wms_tile_cache: createPVSTileService<CoordinateSystem.EPSG3857>({
    label: "Spatiotemporal PNG Tile Cache",
    maxSize: cacheLimit(MiB(750), CACHE_PROFILE),
    formatSize: (val) => `${val} MiB`,
    delayEvictionUntilSize: cacheLimit(MiB(800), CACHE_PROFILE),
    tileSize: TILE_SIZE,
    tileGetter: createWmsTileGetter<CoordinateSystem.EPSG3857>(),
  }),

  /**
   * A tile service to allow requesting multiple images for a single tile.
   * Use this cache if you need multiple WMS images for a single tile.
   */
  multi_wms_tile_cache: createPVSTileService<CoordinateSystem.EPSG3857>({
    label:
      "Spatiotemporal Multi PNG Tile Tile Cache. Allows requesting multiple images for multiple parameters for a single tile",
    maxSize: cacheLimit(MiB(750), CACHE_PROFILE),
    formatSize: (val) => `${val} MiB`,
    delayEvictionUntilSize: cacheLimit(MiB(800), CACHE_PROFILE),
    tileSize: TILE_SIZE,
    tileGetter: createMultiWmsTileGetter<CoordinateSystem.EPSG3857>(),
  }),

  multi_wms_slice_cache: createPVSTileService<CoordinateSystem.EPSG4326>({
    label:
      "Spatiotemporal Multi PNG Slice Cache. Allows requesting multiple images for multiple parameters for a single slice",
    maxSize: cacheLimit(MiB(750), CACHE_PROFILE),
    formatSize: (val) => `${val} MiB`,
    delayEvictionUntilSize: cacheLimit(MiB(800), CACHE_PROFILE),
    tileSize: TILE_SIZE,
    tileGetter: createMultiWmsTileGetter<CoordinateSystem.EPSG4326>(),
  }),

  spatiotemporal_vector_tile_cache: new VectorTileCache(
    cacheLimit(VECTOR_TILE_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(VECTOR_TILE_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
    cacheLimit(VECTOR_TILE_STYLE_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(VECTOR_TILE_STYLE_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  // let the cache outgrow its size by 10% before triggering the eviction routine.
  //
  // the largest colormap that we have is 6000 samples large. 500 KiB corresponds to 21 of these
  // colormaps. Most other colormaps have less than 1000 samples. Thereferoe, these values should
  // be enough. 50KiB corresponds to 12800 samples. So, at least two colormaps will be evicted
  // at once.
  colormap_cache: new ColorMapCache(
    "Colormap Cache",
    cacheLimit(KiB(500), CACHE_PROFILE),
    cacheLimit(KiB(550), CACHE_PROFILE),
  ),
  // [DEBUG-CACHE-MEMORY] // Note: this is so low that the application will crash for some tabs because the active working set is evicted.
  // [DEBUG-CACHE-MEMORY] colormap_cache: new ColorMapCache("Colormap Cache", KiB(1), KiB(2)),
  geojson_cache: new GeoJSONCache(
    "GeoJSON Cache",
    cacheLimit(GEOJSON_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(GEOJSON_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),
  // Cache for holding custom GeoJSON, sliced by feature types
  custom_geojson_cache: new CustomGeoJSONCache(
    "Custom GeoJSON Cache",
    cacheLimit(CUSTOM_GEOJSON_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(CUSTOM_GEOJSON_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
    cacheLimit(CUSTOM_GEOJSON_GEOMETRY_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(CUSTOM_GEOJSON_GEOMETRY_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  poi_cache: new PoiCache(
    "POI Cache",
    cacheLimit(POI_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(POI_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
    cacheLimit(POI_DATA_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(POI_DATA_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  capabilities_cache: new CapabilitiesCache(
    "WFS GetCapabilities Cache",
    cacheLimit(CAPABILITIES_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(CAPABILITIES_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  pointJson_cache: new PointJSONCache(
    "Point Cache",
    cacheLimit(POINT_JSON_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(POINT_JSON_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),
  polygonJson_cache: new PolygonJSONCache(
    "Polygon Cache",
    cacheLimit(POLYGON_JSON_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(POLYGON_JSON_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  plot_cache: new PlotCache(
    "Plot Cache",
    cacheLimit(PLOT_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(PLOT_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  initDate_cache: new InitDateCache(
    "Init Date Cache",
    cacheLimit(INIT_DATE_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(INIT_DATE_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),

  availableTimeFrame_cache: new AvailableTimeFrameCache("Available Time Frame Cache"),

  maptiler_sprite_cache: new MaptilerSpritesCache(
    "Maptiler Sprite cache",
    cacheLimit(MAPTILER_SPRITE_CACHE_MAX_SIZE, CACHE_PROFILE),
    cacheLimit(MAPTILER_SPRITE_CACHE_DELAYED_EVICTION_SIZE, CACHE_PROFILE),
  ),
  // Note: We would like to move most caches into this Cache (wms / tiling cache should still have an own)
  generic_cache: new GenericCache("Generic Cache"),
};

export const resetCacheData = () => {
  const cachesToSkip = ["colormap_cache"];
  for (const [cacheKey, cacheValue] of Object.entries(networkCaches)) {
    if (!cachesToSkip.includes(cacheKey)) {
      cacheValue?.clear?.();
    }
  }
};
